import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Rating,
  Avatar,
  Chip,
  Button,
  IconButton,
  Divider,
  Alert,
  LinearProgress,
  Stack,
  Badge,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Container,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fade,
  Skeleton,
  CircularProgress,
  Snackbar,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch
} from '@mui/material';
import {
  Star as StarIcon,
  Reply as ReplyIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Psychology as AIIcon,
  Send as SendIcon,
  Edit as EditIcon,
  AutoAwesome as MagicIcon,
  QrCode2 as QrCodeIcon,
  Link as LinkIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  Delete as DeleteIcon,
  NotificationImportant as NotificationIcon
} from '@mui/icons-material';

const ReviewsManager = () => {
  const { locationId } = useParams();
  const { selectedBusiness } = useBusiness();
  const [reviewsData, setReviewsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [businessData, setBusinessData] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const REVIEWS_PER_PAGE = 10;

  // Reply dialog state
  const [replyDialog, setReplyDialog] = useState({
    open: false,
    review: null,
    text: '',
    isGeneratingAI: false,
    selectedTone: 'professional'
  });

  // Filtering and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRating, setFilterRating] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [showUnansweredOnly, setShowUnansweredOnly] = useState(false);
  const [filteredReviews, setFilteredReviews] = useState([]);

  // UI state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [reviewUrlDialogOpen, setReviewUrlDialogOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('d.nawaz@am-beratung.de');
  const [emailMode, setEmailMode] = useState('custom'); // 'custom' or 'default'
  const [ownerNotificationOpen, setOwnerNotificationOpen] = useState(false);
  const [ownerNotificationReview, setOwnerNotificationReview] = useState(null);
  const [ownerNotificationRecipient, setOwnerNotificationRecipient] = useState('d.nawaz@am-beratung.de');
  const [ownerNotificationSending, setOwnerNotificationSending] = useState(false);
  const [ownerNotificationEmailContent, setOwnerNotificationEmailContent] = useState('');
  const [reviewMappingDialogOpen, setReviewMappingDialogOpen] = useState(false);

  // Fetch business data for review URL and QR code
  const fetchBusinessData = async () => {
    if (locationId) {
      try {
        console.log('Fetching business data for locationId:', locationId);
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`/api/businesses/${locationId}/details`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          console.log('Business data fetched:', data);
          console.log('Has reviewUrl:', !!data.reviewUrl);
          console.log('Has qrCodeUrl:', !!data.qrCodeUrl);
          console.log('Has reviewMapping:', !!data.reviewMapping);
          console.log('ReviewMapping length:', data.reviewMapping?.length || 0);
          console.log('All available keys:', Object.keys(data));
          console.log('ReviewUrl value:', data.reviewUrl);
          console.log('QrCodeUrl value:', data.qrCodeUrl);
          console.log('ReviewMapping value:', data.reviewMapping);
          setBusinessData(data);
        } else {
          console.error('Failed to fetch business data:', response.status, response.statusText);
        }
      } catch (err) {
        console.error('Error fetching business data:', err);
      }
    }
  };

  useEffect(() => {
    if (locationId) {
      fetchReviews();
      fetchBusinessData();
    }
  }, [locationId]);

  useEffect(() => {
    if (selectedBusiness && locationId) {
      const businessLocationId = selectedBusiness.locationId || selectedBusiness.id;
      if (businessLocationId === locationId) {
        console.log('Business context updated in Reviews:', {
          businessName: selectedBusiness.businessName,
          locationId: businessLocationId
        });
      }
    }
  }, [selectedBusiness, locationId]);

  // Fetch owner notification email content when dialog opens
  useEffect(() => {
    if (ownerNotificationOpen && ownerNotificationReview) {
      getOwnerNotificationEmailContent(ownerNotificationReview).then(content => {
        setOwnerNotificationEmailContent(content);
      });
    }
  }, [ownerNotificationOpen, ownerNotificationReview]);

  // Filter & sort + reset page on change
  useEffect(() => {
    if (!reviewsData?.reviews) return;

    let filtered = [...reviewsData.reviews];

    // Build set of notified review IDs from businessData.reviewMapping
    const notifiedReviewIds = new Set();
    if (businessData?.reviewMapping && Array.isArray(businessData.reviewMapping)) {
      businessData.reviewMapping.forEach(review => {
        if (review.Notified === true) {
          notifiedReviewIds.add(review.reviewId);
        }
      });
    }

    // Unanswered toggle - takes priority
    if (showUnansweredOnly) {
      filtered = filtered.filter(r => {
        // Check if this review ID is in the notified set
        let reviewId = r.id;
        if (r.id.includes('/reviews/')) {
          reviewId = r.id.split('/reviews/')[1];
        }
        // Show only unreplied reviews that are NOT notified
        return !r.isReplied && !notifiedReviewIds.has(reviewId);
      });
    } else {
      // Search
      if (searchTerm) {
        filtered = filtered.filter(review =>
          (review.text?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
          review.author.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Rating filter
      if (filterRating !== 'all') {
        const ratingNum = parseInt(filterRating);
        filtered = filtered.filter(review => review.rating === ratingNum);
      }

      // Status filter
      if (filterStatus !== 'all') {
        if (filterStatus === 'replied') filtered = filtered.filter(r => r.isReplied);
        if (filterStatus === 'unreplied') filtered = filtered.filter(r => !r.isReplied);
        if (filterStatus === 'new') filtered = filtered.filter(r => r.isNew);
      }
    }

    // Sorting - use 'oldest' when showing unanswered only
    const sortToUse = showUnansweredOnly ? 'oldest' : sortBy;
    filtered.sort((a, b) => {
      switch (sortToUse) {
        case 'newest': return new Date(b.time || 0) - new Date(a.time || 0);
        case 'oldest': return new Date(a.time || 0) - new Date(b.time || 0);
        case 'highest': return b.rating - a.rating;
        case 'lowest': return a.rating - b.rating;
        default: return 0;
      }
    });

    setFilteredReviews(filtered);
    setCurrentPage(1); // Reset to first page on filter change
  }, [reviewsData, searchTerm, filterRating, filterStatus, sortBy, showUnansweredOnly, businessData?.reviewMapping]);

  // Pagination logic
  const paginatedReviews = useMemo(() => {
    const start = (currentPage - 1) * REVIEWS_PER_PAGE;
    const end = start + REVIEWS_PER_PAGE;
    return filteredReviews.slice(start, end);
  }, [filteredReviews, currentPage]);

  const totalPages = Math.ceil(filteredReviews.length / REVIEWS_PER_PAGE);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/reviews/${locationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

      const data = await response.json();
      setReviewsData(data);
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
      setError(`Fehler beim Laden der Bewertungen: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleReplyClick = (review) => {
    setReplyDialog({
      open: true,
      review,
      text: '',
      isGeneratingAI: false,
      selectedTone: 'professional'
    });
  };

  const handleGenerateAIResponse = async () => {
    if (!replyDialog.review) return;

    setReplyDialog(prev => ({ ...prev, isGeneratingAI: true }));

    try {
      const accountId = reviewsData?.location?.accountId;
      if (!accountId) throw new Error('Account ID nicht verfügbar');

      const fullReviewId = replyDialog.review.id;
      const reviewIdMatch = fullReviewId.match(/reviews\/([^\/]+)$/);
      const actualReviewId = reviewIdMatch ? reviewIdMatch[1] : fullReviewId;

      const response = await fetch(`/api/reviews/accounts/${accountId}/locations/${locationId}/reviews/${actualReviewId}/generate-response`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tone: replyDialog.selectedTone,
          businessData: {
            business_name: reviewsData?.location?.name || 'Ihr Unternehmen',
            categories: [{ displayName: 'Unternehmen' }]
          }
        })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (data.success) {
        setReplyDialog(prev => ({ ...prev, text: data.data.generatedResponse, isGeneratingAI: false }));
        setSnackbar({ open: true, message: 'KI-Antwort erfolgreich generiert!', severity: 'success' });
      } else {
        throw new Error(data.error || 'Unbekannter Fehler');
      }
    } catch (error) {
      console.error('Error generating AI response:', error);
      setSnackbar({ open: true, message: `Fehler bei KI-Generierung: ${error.message}`, severity: 'error' });
      setReplyDialog(prev => ({ ...prev, isGeneratingAI: false }));
    }
  };

  const handleReplySubmit = async () => {
    if (!replyDialog.text.trim()) return;

    try {
      console.log('Submitting reply:', replyDialog.review.id, replyDialog.text);

      const accountId = reviewsData?.location?.accountId;
      if (!accountId) throw new Error('Account ID nicht verfügbar');

      const fullReviewId = replyDialog.review.id;
      const reviewIdMatch = fullReviewId.match(/reviews\/([^\/]+)$/);
      const actualReviewId = reviewIdMatch ? reviewIdMatch[1] : fullReviewId;

      const response = await fetch(`/api/reviews/accounts/${accountId}/locations/${locationId}/reviews/${actualReviewId}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reply: replyDialog.text
        })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (data.success) {
        setSnackbar({ open: true, message: 'Antwort erfolgreich versendet!', severity: 'success' });
        setReplyDialog({ open: false, review: null, text: '', isGeneratingAI: false, selectedTone: 'professional' });
        await fetchReviews();
      } else {
        throw new Error(data.error || 'Unbekannter Fehler');
      }
    } catch (error) {
      console.error('Error submitting reply:', error);
      setSnackbar({ open: true, message: `Fehler beim Senden: ${error.message}`, severity: 'error' });
    }
  };

  const handleDeleteReply = async (review) => {
    if (!window.confirm('Möchten Sie diese Antwort wirklich löschen?')) {
      return;
    }

    try {
      const accountId = reviewsData?.location?.accountId;
      if (!accountId) throw new Error('Account ID nicht verfügbar');

      const fullReviewId = review.id;
      const reviewIdMatch = fullReviewId.match(/reviews\/([^\/]+)$/);
      const actualReviewId = reviewIdMatch ? reviewIdMatch[1] : fullReviewId;

      const response = await fetch(`/api/reviews/accounts/${accountId}/locations/${locationId}/reviews/${actualReviewId}/reply`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (data.success) {
        setSnackbar({ open: true, message: 'Antwort erfolgreich gelöscht!', severity: 'success' });
        await fetchReviews();
      } else {
        throw new Error(data.error || 'Fehler beim Löschen der Antwort');
      }
    } catch (error) {
      console.error('Error deleting reply:', error);
      setSnackbar({ open: true, message: `Fehler beim Löschen: ${error.message}`, severity: 'error' });
    }
  };

  // Handle review URL and QR code functionality
  const handleCopyReviewLink = async () => {
    if (businessData?.reviewUrl) {
      try {
        await navigator.clipboard.writeText(businessData.reviewUrl);
        setSnackbar({ open: true, message: 'Bewertungslink in Zwischenablage kopiert!', severity: 'success' });
      } catch (err) {
        console.error('Failed to copy review URL:', err);
        setSnackbar({ open: true, message: 'Fehler beim Kopieren des Links', severity: 'error' });
      }
    }
  };

  const handleDownloadQR = () => {
    if (businessData?.qrCodeUrl) {
      const link = document.createElement('a');
      link.href = businessData.qrCodeUrl;
      link.download = `qr-code-${businessData.businessName || 'review'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setSnackbar({ open: true, message: 'QR-Code heruntergeladen!', severity: 'success' });
    }
  };

  const handleEmailPreview = () => {
    if (!businessData?.reviewUrl || !businessData?.qrCodeUrl) {
      setSnackbar({ open: true, message: 'Bewertungslink oder QR-Code nicht verfügbar', severity: 'error' });
      return;
    }
    // Set default recipient based on mode
    if (emailMode === 'default' && businessData?.email) {
      setEmailRecipient(businessData.email);
    } else {
      setEmailRecipient('d.nawaz@am-beratung.de');
    }
    setEmailPreviewOpen(true);
  };

  const handleEmailModeChange = (event) => {
    const newMode = event.target.value;
    setEmailMode(newMode);
    
    if (newMode === 'default' && businessData?.email) {
      setEmailRecipient(businessData.email);
    } else {
      setEmailRecipient('d.nawaz@am-beratung.de');
    }
  };

  const handleSendEmail = async () => {
    setEmailSending(true);

    try {
      const response = await fetch('/api/send-review-promotion-email', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          businessData: {
            businessName: businessData.businessName,
            reviewUrl: businessData.reviewUrl,
            qrCodeUrl: businessData.qrCodeUrl
          },
          recipient: emailRecipient
        })
      });

      if (response.ok) {
        const result = await response.json();
        setSnackbar({ open: true, message: `E-Mail erfolgreich an ${emailRecipient} versendet!`, severity: 'success' });
        setEmailPreviewOpen(false);
      } else {
        throw new Error('E-Mail Versand fehlgeschlagen');
      }
    } catch (error) {
      console.error('Email sending error:', error);
      setSnackbar({ open: true, message: `Fehler beim E-Mail Versand: ${error.message}`, severity: 'error' });
    } finally {
      setEmailSending(false);
    }
  };

  const handleOwnerNotification = (review) => {
    setOwnerNotificationReview(review);
    setOwnerNotificationRecipient(businessData?.email || 'd.nawaz@am-beratung.de');
    setOwnerNotificationOpen(true);
  };

  const getOwnerNotificationEmailContent = async (review) => {
    if (!review || !businessData) return '';

    let firstName = businessData.businessName?.split(' ')[0] || 'Unternehmer';

  
    // Fetch HubSpot contact to get first name using contactCloudTenantId
    if (businessData.contactCloudTenantId) {
      try {
        const token = localStorage.getItem('auth_token');
        console.log('📞 Searching HubSpot contact with contactcloud_tenant_id:', businessData.contactCloudTenantId);

        // Search for contact where contactcloud_tenant_id property matches
        const hubspotResponse = await fetch('/api/hubspot/contacts/search', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            contactCloudTenantId: businessData.contactCloudTenantId
          })
        });

        console.log('📞 HubSpot response status:', hubspotResponse.status);
        if (hubspotResponse.ok) {
          const contactResponse = await hubspotResponse.json();
          console.log('📞 HubSpot search response:', contactResponse);

          if (contactResponse.results && contactResponse.results.length > 0) {
            const contact = contactResponse.results[0];
            if (contact.properties?.firstname) {
              firstName = contact.properties.firstname;
              console.log('✅ FirstName found:', firstName);
            } else {
              console.log('⚠️ FirstName property not found in HubSpot contact');
            }
          } else {
            console.log('⚠️ No HubSpot contact found with contactcloud_tenant_id:', businessData.contactCloudTenantId);
          }
        } else {
          console.warn('📞 HubSpot API error:', hubspotResponse.status, hubspotResponse.statusText);
        }
      } catch (error) {
        console.warn('Failed to fetch HubSpot contact firstname:', error);
      }
    } else {
      console.warn('⚠️ No contactCloudTenantId found in businessData');
    }

    const stars = review.rating;
    const reviewerName = review.author.name || 'Unbekannt';
    const reviewDate = new Date(review.time).toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' });
    const reviewText = review.text || '';

    return `Hallo ${firstName},

im Zuge der Optimierung deines Google-Unternehmensprofils ist uns aufgefallen, dass eine negative Bewertung in deinem Profil veröffentlicht wurde.

Bewertung:
${stars} Sterne

Von:
${reviewerName}

Datum:
${reviewDate}

Inhalt:
${reviewText}

Da wir wissen, wie entscheidend der erste Eindruck für potenzielle Interessenten ist, möchten wir dich darauf aufmerksam machen, dass solche Bewertungen die Anfragequote spürbar beeinflussen können.

Die gute Nachricht: Heutzutage gibt es Möglichkeiten, gegen unzutreffende oder angreifbare Bewertungen vorzugehen. Wir arbeiten hierbei mit einem spezialisierten Partner zusammen, der sich auf die Entfernung solcher Bewertungen fokussiert hat.

Die Abwicklung erfolgt unkompliziert und in der Regel erfolgsbasiert. Du zahlst also nur, wenn die Bewertung tatsächlich entfernt wird.

Wenn du dir diese Möglichkeit unverbindlich anschauen möchtest, kannst du hier direkt einen Termin vereinbaren:

https://calendly.com/c-simbach/kostenfreie-besprechung-ihrer-bewertungen-neu

Bei Fragen melde dich jederzeit gerne.

Viele Grüße,
A&M`;
  };

  const handleSendOwnerNotification = async () => {
    if (!ownerNotificationRecipient.trim()) return;

    setOwnerNotificationSending(true);

    try {
      const emailBody = await getOwnerNotificationEmailContent(ownerNotificationReview);

      const response = await fetch('/api/send-owner-notification-email', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          locationId: locationId,
          businessData: {
            businessName: businessData.businessName
          },
          review: {
            id: ownerNotificationReview.id,
            rating: ownerNotificationReview.rating,
            authorName: ownerNotificationReview.author.name,
            time: ownerNotificationReview.time,
            text: ownerNotificationReview.text
          },
          recipient: ownerNotificationRecipient,
          subject: 'Hinweis zu deinen Google-Bewertungen',
          body: emailBody
        })
      });

      if (response.ok) {
        const result = await response.json();
        setSnackbar({ open: true, message: `E-Mail erfolgreich an ${ownerNotificationRecipient} versendet!`, severity: 'success' });
        setOwnerNotificationOpen(false);
        // Refresh reviews to update the Notified status
        fetchReviews();
      } else {
        throw new Error('E-Mail Versand fehlgeschlagen');
      }
    } catch (error) {
      console.error('Owner notification email error:', error);
      setSnackbar({ open: true, message: `Fehler beim E-Mail Versand: ${error.message}`, severity: 'error' });
    } finally {
      setOwnerNotificationSending(false);
    }
  };

  const getEmailPreviewContent = () => {
    if (!businessData?.reviewUrl) return '';
    
    return `Guten Tag,

positive Rezensionen sind einer der wichtigsten Faktoren für dein Ranking bei Google Business Profile. Sie signalisieren Google Relevanz und Vertrauenswürdigkeit, wodurch dein Unternehmen in der lokalen Suche weiter oben platziert wird und mehr Sichtbarkeit bei Neukunden erhält.

Damit du künftig schneller und einfacher Feedback einsammeln kannst, haben wir dir die direkten Zugänge vorbereitet. Im Anhang findest du deinen individuellen QR-Code sowie den passenden Direkt-Link.

Unsere Empfehlung für dich:
Um die Anzahl deiner Bewertungen kurzfristig zu steigern, empfehlen wir dir folgende Schritte:

Der direkte Link: Versende den untenstehenden Link per E-Mail oder WhatsApp direkt nach einem erfolgreichen Projektabschluss oder Kauf.

Der QR-Code: Drucke den beigefügten QR-Code auf deine Rechnungen, Lieferscheine oder stelle einen kleinen Aufsteller in deinen Geschäftsräumen auf.

Persönliche Ansprache: Bitte zufriedene Kunden aktiv um einen kurzen Moment Zeit – die Hürde ist durch den Direktzugriff nun minimal, da das Formular sofort öffnet.

Dein Link zum Kopieren:
${businessData.reviewUrl}

Viel Erfolg beim Einsammeln der Rezensionen!

Beste Grüße,
A&M`;
  };

  const formatRatingDistribution = (distribution) => {
    const total = Object.values(distribution).reduce((sum, count) => sum + count, 0);
    return Object.entries(distribution)
      .reverse()
      .map(([stars, count]) => ({
        stars: parseInt(stars),
        count,
        percentage: total > 0 ? Math.round((count / total) * 100) : 0
      }));
  };

  const getReviewStatus = (rating) => {
    if (rating >= 4.5) return 'excellent';
    if (rating >= 3.5) return 'good';
    if (rating >= 2.5) return 'average';
    return 'poor';
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" sx={{ fontSize: '2rem', mb: 2, width: '300px' }} />
          <Skeleton variant="text" sx={{ fontSize: '1rem', mb: 3, width: '200px' }} />
        </Box>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} md={3} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" sx={{ fontSize: '1.5rem', mb: 2 }} />
                  <Skeleton variant="rectangular" width="100%" height={60} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <LinearProgress sx={{ borderRadius: 1, height: 6 }} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchReviews}>
              <RefreshIcon sx={{ mr: 1 }} />
              Erneut versuchen
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (!reviewsData) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Keine Bewertungsdaten verfügbar.
        </Alert>
      </Container>
    );
  }

  const { statistics, reviews, location } = reviewsData;
  const ratingStatus = getReviewStatus(statistics.averageRating);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, backgroundColor: 'white', border: '1px solid #E0E0E0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {/* Left side button */}
            <Button
              variant="outlined"
              startIcon={<NotificationIcon />}
              onClick={() => setReviewMappingDialogOpen(true)}
              sx={{
                borderColor: '#ff9800',
                color: '#ff9800',
                borderRadius: 2,
                px: 2,
                alignSelf: 'flex-start',
                mt: 1,
                '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.04)' }
              }}
            >
              Bewertungslink
            </Button>
            <Box>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 1, color: '#000000' }}>
                Bewertungsmanagement
              </Typography>
              <Typography variant="h6" sx={{ color: '#605D5C', fontWeight: 400 }}>
                {location?.name || 'Ihr Unternehmen'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#959595', mt: 1 }}>
                Verwalten Sie alle Kundenbewertungen zentral und professionell
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={2}>
            {/* Review URL and QR Code Buttons - Always show for debugging */}
            <Button
              variant="contained"
              startIcon={<LinkIcon />}
              onClick={() => {
                console.log('Review button clicked. BusinessData:', businessData);
                setReviewUrlDialogOpen(true);
              }}
              sx={{ backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' }, borderRadius: 2, px: 3 }}
            >
              Bewertungslink
            </Button>
            <Button
              variant="outlined"
              startIcon={<QrCodeIcon />}
              onClick={() => {
                console.log('QR button clicked. BusinessData:', businessData);
                setQrDialogOpen(true);
              }}
              sx={{ borderColor: '#1976d2', color: '#1976d2', borderRadius: 2, px: 3 }}
            >
              QR-Code
            </Button>
            <Button
              variant="contained"
              startIcon={<EmailIcon />}
              onClick={handleEmailPreview}
              disabled={!businessData?.reviewUrl || !businessData?.qrCodeUrl}
              sx={{ backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#1b5e20' }, borderRadius: 2, px: 3 }}
            >
              E-Mail senden
            </Button>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchReviews}
              sx={{ backgroundColor: '#C41E3A', '&:hover': { backgroundColor: '#A01729' }, borderRadius: 2, px: 3 }}
            >
              Aktualisieren
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {[
            { label: 'Durchschnitt', value: statistics.averageRating?.toFixed(1) || '0.0' },
            { label: 'Bewertungen', value: statistics.totalReviews || 0 },
            { label: 'Antwortrate', value: `${statistics.responseRate || 0}%` },
            { label: 'Unbeantwortet', value: statistics.unansweredReviews || 0, highlight: statistics.unansweredReviews > 0 }
          ].map((stat, i) => (
            <Grid item xs={6} md={3} key={i}>
              <Paper elevation={0} sx={{
                p: 3, textAlign: 'center', backgroundColor: '#FAFAFA',
                border: stat.highlight ? '2px solid #FF9800' : '1px solid #E0E0E0',
                borderRadius: 2,
                transition: 'all 0.2s',
                '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.1)', transform: 'translateY(-2px)' }
              }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: stat.highlight ? '#FF9800' : '#000000' }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" sx={{ color: '#605D5C', fontWeight: 500 }}>
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth placeholder="Bewertungen durchsuchen..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              variant="outlined" size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small" disabled={showUnansweredOnly}>
              <InputLabel>Sterne</InputLabel>
              <Select value={filterRating} onChange={(e) => setFilterRating(e.target.value)} label="Sterne">
                <MenuItem value="all">Alle Sterne</MenuItem>
                {[5,4,3,2,1].map(n => <MenuItem key={n} value={n}>{n} Sterne</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small" disabled={showUnansweredOnly}>
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} label="Status">
                <MenuItem value="all">Alle Status</MenuItem>
                <MenuItem value="unreplied">Unbeantwortet</MenuItem>
                <MenuItem value="replied">Beantwortet</MenuItem>
                <MenuItem value="new">Neu (7 Tage)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small" disabled={showUnansweredOnly}>
             <InputLabel>Sortierung</InputLabel>
              <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} label="Sortierung">
                <MenuItem value="newest">Neueste zuerst</MenuItem>
                <MenuItem value="oldest">Älteste zuerst</MenuItem>
                <MenuItem value="highest">Höchste zuerst</MenuItem>
                <MenuItem value="lowest">Niedrigste zuerst</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative' }}>
              <button
                onClick={() => setShowUnansweredOnly(!showUnansweredOnly)}
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  width: '1px',
                  height: '1px',
                  opacity: 0,
                  pointerEvents: 'none'
                }}
              >
                workflow-mode
              </button>
              <Box
                role="button"
                onClick={() => setShowUnansweredOnly(!showUnansweredOnly)}
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={showUnansweredOnly}
                      onChange={(e) => setShowUnansweredOnly(e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2" sx={{ fontWeight: 500 }}>workflow-mode</Typography>}
                  sx={{ whiteSpace: 'nowrap', m: 0 }}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={2}>
            <Chip label={`${filteredReviews.length} Ergebnisse`} color="primary" variant="outlined" />
          </Grid>
        </Grid>
      </Paper>

      {/* Results Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Bewertungen
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Zeige {(currentPage - 1) * REVIEWS_PER_PAGE + 1}–{Math.min(currentPage * REVIEWS_PER_PAGE, filteredReviews.length)} von {filteredReviews.length} Ergebnissen
            {searchTerm || filterRating !== 'all' || filterStatus !== 'all' || showUnansweredOnly ? ' (gefiltert)' : ''}
          </Typography>
        </Box>
      </Box>

      {/* Reviews List */}
      {filteredReviews.length === 0 ? (
        <Paper elevation={1} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <WarningIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6">Keine Bewertungen gefunden</Typography>
          <Typography variant="body2" color="text.secondary">
            {reviews.length === 0 ? 'Noch keine Bewertungen vorhanden.' : 'Passen Sie Ihre Filter an.'}
          </Typography>
        </Paper>
      ) : (
        <>
          <Stack spacing={3}>
            {paginatedReviews.map((review) => (
              <Fade in key={review.id}>
                <Paper elevation={2} sx={{
                  p: 4, borderRadius: 3,
                  border: `1px solid ${review.rating <= 2 ? '#ffebee' : review.rating >= 4 ? '#e8f5e8' : '#fff3e0'}`,
                  bgcolor: review.rating <= 2 ? '#fafafa' : 'white',
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Avatar src={review.author.photoUrl} sx={{ width: 56, height: 56 }}>
                        {review.author.name[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>{review.author.name}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                          <Rating value={review.rating} readOnly size="small" sx={{ color: '#C41E3A' }} />
                          <Typography variant="body2" color="text.secondary">{review.timeDescription}</Typography>
                          {review.isNew && <Chip label="NEU" size="small" color="primary" sx={{ fontWeight: 'bold' }} />}
                        </Box>
                        <Chip label={review.time.split('T')[0]} size="small" variant="outlined" sx={{ mt: 1 }} />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                      {review.isReplied ? (
                        <Chip icon={<CheckCircleIcon />} label="Beantwortet" color="success" variant="outlined" size="small" />
                      ) : (
                        <Button variant="contained" size="small" startIcon={<ReplyIcon />}
                          onClick={() => handleReplyClick(review)}
                          sx={{ bgcolor: '#C41E3A', '&:hover': { bgcolor: '#a01729' } }}>
                          Antworten
                        </Button>
                      )}
                      <Button variant="outlined" size="small" startIcon={<NotificationIcon />}
                        onClick={() => handleOwnerNotification(review)}
                        sx={{ borderColor: '#FF6F00', color: '#FF6F00', '&:hover': { bgcolor: 'rgba(255, 111, 0, 0.1)' } }}>
                        Besitzer benachrichtigen
                      </Button>
                    </Box>
                  </Box>

                  {review.text && (
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 3, fontStyle: 'italic' }}>
                      "{review.text}"
                    </Typography>
                  )}

                  {review.reply && (
                    <Box sx={{ ml: 2, p: 3, bgcolor: '#f8fbf8', borderRadius: 2, borderLeft: '4px solid #4caf50' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon color="success" />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                            Antwort vom Unternehmen • {review.reply.timeDescription}
                          </Typography>
                        </Box>
                        <Tooltip title="Antwort löschen">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteReply(review)}
                            sx={{ color: '#d32f2f', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography>{review.reply.text}</Typography>
                    </Box>
                  )}
                </Paper>
              </Fade>
            ))}
          </Stack>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
              <Paper elevation calm={1} sx={{ borderRadius: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
                  <Button disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
                    Zurück
                  </Button>

                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    let page;
                    if (totalPages <= 7) page = i + 1;
                    else if (currentPage <= 4) page = i + 1;
                    else if (currentPage > totalPages - 4) page = totalPages - 6 + i;
                    else page = currentPage - 3 + i;

                    if (page < 1 || page > totalPages) return null;

                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? "contained" : "text"}
                        onClick={() => handlePageChange(page)}
                        sx={{
                          minWidth: 40,
                          bgcolor: currentPage === page ? '#C41E3A' : 'transparent',
                          color: currentPage === page ? 'white' : 'text.primary',
                          '&:hover': { bgcolor: currentPage === page ? '#a01729' : 'rgba(0,0,0,0.04)' }
                        }}
                      >
                        {page}
                      </Button>
                    );
                  }).filter(Boolean)}

                  <Button disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                    Weiter
                  </Button>
                </Stack>
              </Paper>
            </Box>
          )}
        </>
      )}

      {/* Reply Dialog */}
      <Dialog open={replyDialog.open} onClose={() => setReplyDialog({ open: false, review: null, text: '', isGeneratingAI: false, selectedTone: 'professional' })}
        maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ReplyIcon color="primary" />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>Auf Bewertung antworten</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {replyDialog.review && (
            <>
              <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Avatar src={replyDialog.review.author.photoUrl} sx={{ width: 48, height: 48 }}>
                    {replyDialog.review.author.name[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{replyDialog.review.author.name}</Typography>
                    <Rating value={replyDialog.review.rating} readOnly size="small" />
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ fontStyle: 'italic' }}>"{replyDialog.review.text}"</Typography>
              </Paper>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>KI-Antwort generieren</Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Tonfall</InputLabel>
                    <Select value={replyDialog.selectedTone}
                      onChange={(e) => setReplyDialog(prev => ({ ...prev, selectedTone: e.target.value }))} label="Tonfall">
                      <MenuItem value="professional">Professionell</MenuItem>
                      <MenuItem value="friendly">Freundlich</MenuItem>
                      <MenuItem value="empathetic">Einfühlsam</MenuItem>
                    </Select>
                  </FormControl>
                  <Button variant="outlined" startIcon={replyDialog.isGeneratingAI ? <CircularProgress size={16} /> : <MagicIcon />}
                    onClick={handleGenerateAIResponse} disabled={replyDialog.isGeneratingAI}
                    sx={{ borderColor: '#C41E3A', color: '#C41E3A' }}>
                    {replyDialog.isGeneratingAI ? 'Generiere...' : 'KI generieren'}
                  </Button>
                </Box>
              </Box>

              <TextField fullWidth multiline rows={6} label="Ihre Antwort" placeholder="Antwort eingeben oder KI generieren..."
                value={replyDialog.text}
                onChange={(e) => setReplyDialog(prev => ({ ...prev, text: e.target.value }))}
                variant="outlined"
              />
              {replyDialog.text && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Zeichen: {replyDialog.text.length} / 500 empfohlen
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setReplyDialog({ open: false, review: null, text: '', isGeneratingAI: false, selectedTone: 'professional' })}>
            Abbrechen
          </Button>
          <Button variant="contained" onClick={handleReplySubmit} disabled={!replyDialog.text.trim()}
            startIcon={<SendIcon />} sx={{ bgcolor: '#C41E3A', '&:hover': { bgcolor: '#a01729' } }}>
            Antwort senden
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review URL Dialog */}
      <Dialog open={reviewUrlDialogOpen} onClose={() => setReviewUrlDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <LinkIcon />
            <Typography variant="h6">Bewertungslink teilen</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3}>
            {businessData?.reviewUrl ? (
              <>
                <Typography variant="body1" gutterBottom>
                  Teilen Sie diesen Link mit Ihren Kunden, um Bewertungen zu sammeln:
                </Typography>
                <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all', color: 'text.secondary' }}>
                    {businessData.reviewUrl}
                  </Typography>
                </Paper>
                <Box display="flex" gap={2} justifyContent="center">
                  <Button 
                    variant="contained" 
                    startIcon={<ShareIcon />}
                    onClick={handleCopyReviewLink}
                  >
                    Link kopieren
                  </Button>
                  <Button 
                    variant="outlined" 
                    startIcon={<LinkIcon />}
                    onClick={() => window.open(businessData.reviewUrl, '_blank')}
                  >
                    Link öffnen
                  </Button>
                </Box>
              </>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <LinkIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                <Typography variant="body1" color="textSecondary" textAlign="center">
                  Kein Bewertungslink verfügbar für dieses Unternehmen
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewUrlDialogOpen(false)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <QrCodeIcon />
            <Typography variant="h6">QR-Code für Bewertungslink</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
            {businessData?.qrCodeUrl ? (
              <>
                <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                  <img 
                    src={businessData.qrCodeUrl} 
                    alt="QR Code für Bewertungen" 
                    style={{ width: '200px', height: '200px', display: 'block' }}
                  />
                </Paper>
                <Typography variant="body2" color="textSecondary" textAlign="center">
                  Scannen Sie diesen QR-Code, um direkt zur Bewertungsseite zu gelangen
                </Typography>
                <Box display="flex" gap={2}>
                  <Button 
                    variant="outlined" 
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadQR}
                  >
                    QR-Code herunterladen
                  </Button>
                  <Button 
                    variant="outlined" 
                    startIcon={<ShareIcon />}
                    onClick={handleCopyReviewLink}
                  >
                    Link kopieren
                  </Button>
                </Box>
              </>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <QrCodeIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                <Typography variant="body1" color="textSecondary" textAlign="center">
                  Kein QR-Code verfügbar für dieses Unternehmen
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Preview Dialog */}
      <Dialog open={emailPreviewOpen} onClose={() => setEmailPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon />
            <Typography variant="h6">E-Mail Vorschau</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Email Mode Selection */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                E-Mail Modus
              </Typography>
              <RadioGroup
                value={emailMode}
                onChange={handleEmailModeChange}
                row
              >
                <FormControlLabel 
                  value="custom" 
                  control={<Radio />} 
                  label="Benutzerdefiniert"
                />
                <FormControlLabel 
                  value="default" 
                  control={<Radio />} 
                  label={`An Unternehmen${businessData?.email ? ` (${businessData.email})` : ' (keine E-Mail hinterlegt)'}`}
                  disabled={!businessData?.email}
                />
              </RadioGroup>
            </Box>

            {/* Recipient Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Empfänger
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                placeholder={emailMode === 'custom' ? 'E-Mail Adresse eingeben' : 'Unternehmens E-Mail Adresse'}
                type="email"
                disabled={emailMode === 'default'}
                helperText={emailMode === 'default' ? 'E-Mail wird an die im Unternehmensprofil hinterlegte Adresse gesendet' : 'Geben Sie eine benutzerdefinierte E-Mail Adresse ein'}
              />
            </Box>

            {/* Subject Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Betreff
              </Typography>
              <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2">
                  Mehr Sichtbarkeit – Google-Bewertungen einfach sammeln!
                </Typography>
              </Paper>
            </Box>

            {/* Email Content Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                E-Mail Inhalt
              </Typography>
              <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                  {getEmailPreviewContent()}
                </Typography>
              </Paper>
            </Box>

            {/* Attachment Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Anhang
              </Typography>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                <img 
                  src={businessData?.qrCodeUrl} 
                  alt="QR Code Anhang" 
                  style={{ width: '60px', height: '60px', borderRadius: '4px' }}
                />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    qr-code-{businessData?.businessName || 'bewertung'}.png
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    QR-Code für direkten Bewertungslink
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEmailPreviewOpen(false)}>
            Abbrechen
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSendEmail}
            disabled={emailSending || !emailRecipient.trim()}
            startIcon={emailSending ? <CircularProgress size={16} /> : <SendIcon />}
            sx={{ backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#1b5e20' } }}
          >
            {emailSending ? 'Sende...' : `E-Mail an ${emailRecipient} senden`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Owner Notification Dialog */}
      <Dialog open={ownerNotificationOpen} onClose={() => setOwnerNotificationOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <NotificationIcon />
            <Typography variant="h6">Besitzer benachrichtigen</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Review Summary */}
            {ownerNotificationReview && (
              <Paper elevation={1} sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, borderLeft: '4px solid #FF6F00' }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Avatar src={ownerNotificationReview.author.photoUrl} sx={{ width: 40, height: 40 }}>
                    {ownerNotificationReview.author.name[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{ownerNotificationReview.author.name}</Typography>
                    <Rating value={ownerNotificationReview.rating} readOnly size="small" />
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666' }}>
                  "{ownerNotificationReview.text}"
                </Typography>
              </Paper>
            )}

            {/* Recipient Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Empfänger
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={ownerNotificationRecipient}
                onChange={(e) => setOwnerNotificationRecipient(e.target.value)}
                placeholder="E-Mail Adresse eingeben"
                type="email"
                helperText="E-Mail wird an diese Adresse versendet"
              />
            </Box>

            {/* Subject Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Betreff
              </Typography>
              <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2">
                  Hinweis zu deinen Google-Bewertungen
                </Typography>
              </Paper>
            </Box>

            {/* Email Content Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                E-Mail Inhalt
              </Typography>
              <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, maxHeight: 400, overflow: 'auto' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6, fontSize: '0.9rem' }}>
                  {ownerNotificationEmailContent}
                </Typography>
              </Paper>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOwnerNotificationOpen(false)}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={handleSendOwnerNotification}
            disabled={ownerNotificationSending || !ownerNotificationRecipient.trim()}
            startIcon={ownerNotificationSending ? <CircularProgress size={16} /> : <SendIcon />}
            sx={{ backgroundColor: '#FF6F00', '&:hover': { backgroundColor: '#E65100' } }}
          >
            {ownerNotificationSending ? 'Sende...' : `E-Mail an ${ownerNotificationRecipient} senden`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Mapping Dialog */}
      <Dialog
        open={reviewMappingDialogOpen}
        onClose={() => setReviewMappingDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, backgroundColor: '#f5f5f5', borderBottom: '1px solid #E0E0E0' }}>
          📋 Unreplied Low-Rated Reviews
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {console.log('🔍 Modal Debug - businessData:', businessData, 'reviewMapping:', businessData?.reviewMapping)}
          {businessData?.reviewMapping && businessData.reviewMapping.length > 0 ? (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              {businessData.reviewMapping.map((review, index) => {
                // Extract short review ID if it contains full path
                const shortId = review.reviewId?.includes('/reviews/')
                  ? review.reviewId.split('/reviews/')[1]
                  : review.reviewId;

                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto auto',
                      alignItems: 'center',
                      gap: 2,
                      p: 1.5,
                      backgroundColor: '#f5f5f5',
                      borderRadius: 1,
                      borderLeft: '3px solid #ff9800'
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                      {review.author || 'Anonymous'}
                    </Typography>
                    <Chip
                      label={review.Notified ? '✓ Notified' : 'Not Notified'}
                      size="small"
                      sx={{
                        backgroundColor: review.Notified ? '#c8e6c9' : '#ffccbc',
                        color: review.Notified ? '#2e7d32' : '#d84315',
                        fontWeight: 600
                      }}
                    />
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#666', textAlign: 'right' }}>
                      {shortId}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#999', textAlign: 'center', py: 3 }}>
              No unreplied low-rated reviews found
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #E0E0E0' }}>
          <Button onClick={() => setReviewMappingDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ReviewsManager;